<template>
    <model-form
        :title="
            experimentGroupTaskTemplate && experimentGroupTaskTemplate.id
                ? `Edit Task for ${getTaskDisplayName}`
                : 'Create Data Collection Task'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    >
        <template v-slot:dayFieldDescription>
            {{ taskDayDescription }}
        </template>
        <template v-slot:inheritedTaskGroupDescription>
            {{ taskDayDescription }}
        </template>
    </model-form>
</template>
<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import _ from "lodash";
import { SavedTaskGroup } from "@/services/taskGroups/taskGroupsAPI";
import { SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import {
    SavedExperimentGroupTaskTemplate,
    taskTemplatesAPI,
} from "@/services/taskTemplates/taskTemplatesAPI";
import {
    buildReadoutGroupOptions,
    findTaskGroupById, getTaskDayDescription,
} from "@/lib/taskUtils";
import { withLoadingScreen } from "@/lib/loading";
import { transformEmptyStringToNull } from "@/lib/utils";
import ModelForm from "@/components/common/models/ModelForm.vue";
import { buildNumberField, buildSelectField, buildSlotField } from "@/lib/vueModelUtils";
import { SavedExperimentGroup } from "@/services/experimentGroups/experimentGroupsAPI";

const { experimentGroup, taskGroups, readoutGroups, experimentGroupTaskTemplate } = defineProps<{
    experimentGroup: SavedExperimentGroup,
    taskGroups: SavedTaskGroup[],
    readoutGroups: SavedReadoutGroup[]
    experimentGroupTaskTemplate: SavedExperimentGroupTaskTemplate | null
}>();

const emit = defineEmits<{
    (e: "saved");
    (e: "closed");
}>();

const taskDayDescription = ref("");
const taskGroupField = ref();
const readoutGroupField = ref();
const overrideTaskGroupDayField = ref();
const dayField = ref();
const inheritedTaskGroupDescriptionField = ref();
const formFields = ref<any>([]);

onMounted(() => {
    buildFormFields();
});

const getTaskDisplayName = computed(() => {
    const taskGroup = findTaskGroupById(
        experimentGroupTaskTemplate?.taskGroupId,
        taskGroups
    );
    if (!taskGroup) {
        return "";
    }
    return `${taskGroup.displayName} on day ${taskGroup.day}`;
});

function getDay() {
    if (!experimentGroupTaskTemplate?.id) {
        return null;
    }

    const taskDay = experimentGroupTaskTemplate.day;
    if (!_.isNil(taskDay)) {
        return taskDay;
    } else {
        return ""; // TODO look up selected task group?
    }
}
function buildFormFields() {
    taskGroupField.value = buildTaskGroupFormField();
    readoutGroupField.value = buildReadoutGroupFormField();
    overrideTaskGroupDayField.value = buildOverrideTaskGroupDayFormField();
    dayField.value = buildSpecifyDayFormField();

    inheritedTaskGroupDescriptionField.value = buildSlotField({
        slot: "inheritedTaskGroupDescription",
        cols: 12,
    });

    updateFormFields();
    updateTaskDayDescription();
}
function buildTaskGroupFormField() {
    const taskGroupOptions = taskGroups.map((taskGroup) => {
        return {
            title: taskGroup.name,
            value: taskGroup.id,
        };
    });

    const taskGroupId = experimentGroupTaskTemplate?.taskGroupId;

    return buildSelectField({
        name: "Task Group",
        options: taskGroupOptions,
        value: taskGroupId,
        changed: (taskGroupId: string) => {
            const taskGroup = findTaskGroupById(taskGroupId, taskGroups);
            updateTaskGroupSelection(taskGroup);
        },
    });
}
function buildReadoutGroupFormField() {
    const readoutGroupOptions = buildReadoutGroupOptions(readoutGroups);

    return buildSelectField({
        name: "Readout Group",
        options: readoutGroupOptions,
        value: experimentGroupTaskTemplate?.readoutGroupId,
    });
}
function buildOverrideTaskGroupDayFormField() {
    const hasOverriddenValue =
        !_.isNil(experimentGroupTaskTemplate?.day);

    const formField = taskGroupField.value;

    console.log(`building overrideTaskGroupDayField with current value of ${hasOverriddenValue}, because experimentGroupTaskTemplate.day == ${experimentGroupTaskTemplate?.day}`);

    return {
        fieldType: "boolean",
        fieldDisplayName: "Override Task Group Day",
        value: hasOverriddenValue,
        disabled: _.isNil(formField.value),
        changed: () => {
            toggleOverrideTaskGroup();
        },
    };
}
function buildSpecifyDayFormField() {
    const taskDay = experimentGroupTaskTemplate?.day;

    const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;
    const overrideTaskGroup: boolean = overrideTaskGroupDayFormField.value;

    const formField = buildNumberField({ label: "Day", value: taskDay });
    formField.value = getDay();
    formField.disabled = !overrideTaskGroup;
    formField.changed = () => {
        updateTaskDayDescription();
    };

    formField.appendSlot = "dayFieldDescription";

    return formField;
}
function updateFormFields() {
    const updatedFormFields = [
        readoutGroupField.value,
        taskGroupField.value,
        overrideTaskGroupDayField.value,
    ];

    const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;
    const overrideTaskGroupDay: boolean = overrideTaskGroupDayFormField.value;

    if (overrideTaskGroupDay) {
        updatedFormFields.push(dayField.value);
    } else {
        // If the user is not overriding the task group day, show the inherited description
        updatedFormFields.push(inheritedTaskGroupDescriptionField.value);
    }

    formFields.value = updatedFormFields;
}
function toggleOverrideTaskGroup() {
    const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;
    const overrideTaskGroup: boolean = overrideTaskGroupDayFormField.value;
    // set the override fields to disabled if not overriding, otherwise enable them.
    dayField.value.disabled = !overrideTaskGroup;
    updateFormFields();
    updateTaskDayDescription();
}
function updateTaskGroupSelection(taskGroup) {
    overrideTaskGroupDayField.value.disabled = false;

    const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;

    if (!overrideTaskGroupDayFormField.value) {
        dayField.value.value = taskGroup.day || 0;
    }

    updateFormFields();
    updateTaskDayDescription();
}
function updateTaskDayDescription() {

    const dayFormField = dayField.value;
    const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;
    const overrideTaskGroupDay: boolean = overrideTaskGroupDayFormField.value;
    const taskGroupFormField = taskGroupField.value;

    taskDayDescription.value = getTaskDayDescription({
        day: dayFormField.value,
        date: null,
        isRelativeDay0: true,
        isInheritedFromTaskGroup: !overrideTaskGroupDay,
        selectedTaskGroupId: taskGroupFormField.value,
        taskGroups: taskGroups,
    });
}
async function save() {
    await withLoadingScreen(async () => {
        const experimentGroupTaskTemplateToSave = {
            ...experimentGroupTaskTemplate!,
        };

        const dayFormField = dayField.value;
        const overrideTaskGroupDayFormField = overrideTaskGroupDayField.value;
        const overrideTaskGroupDay: boolean = overrideTaskGroupDayFormField.value;

        console.log(`saving with overrideTaskGroupDay value of ${overrideTaskGroupDay}`);

        const taskGroupFormField = taskGroupField.value;
        const readoutGroupFormField = readoutGroupField.value;

        if (overrideTaskGroupDay) {
            console.log(`settings experimentGroupTaskTemplateToSave.day to ${dayFormField.value}`);
            const val = transformEmptyStringToNull(dayFormField.value);
            experimentGroupTaskTemplateToSave.day = val ? parseInt(val) : null;
        } else {
            console.log(`settings schemaConfigTaskTemplateToSave.day to null`);
            experimentGroupTaskTemplateToSave.day = null;
        }
        experimentGroupTaskTemplateToSave.taskGroupId = taskGroupFormField.value;
        experimentGroupTaskTemplateToSave.readoutGroupId = readoutGroupFormField.value;
        if (experimentGroupTaskTemplate?.id) {
            await taskTemplatesAPI.byExperimentGroup(experimentGroup.id).update({
                ...experimentGroupTaskTemplateToSave,
                id: experimentGroupTaskTemplate.id!
            });
        } else {
            await taskTemplatesAPI.byExperimentGroup(experimentGroup.id).create(experimentGroupTaskTemplateToSave);
        }
    });
    emit("saved");
}
function cancel() {
    emit("closed");
}

</script>
